import CertificatesDisplay from "./CertificatesDisplay";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Navigation } from "swiper/modules";
import AOS from "aos";
import "aos/dist/aos.css";
import apiClient from "../api/apiClient";
import axios from "axios";
import {
  Calendar,
  Clock,
  DollarSign,
  Users,
  CalendarDays,
  ArrowRight,
} from "lucide-react";

const Home = () => {
  const [groupJour, setGroupJour] = useState([]);
  const [formations, setFormations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [jours, setJours] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const formatHeure = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    return `${hours}:${minutes}`;
  };

  const truncateDescription = (description, maxLength) => {
    return description.length > maxLength
      ? description.substring(0, maxLength) + "..."
      : description;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000,
      );

      const [
        formationsResponse,
        categoriesResponse,
        groupesResponse,
        joursResponse,
        groupJourResponse,
      ] = await Promise.all([
        apiClient.get("/formations", { cancelToken: source.token }),
        apiClient.get("/categorie_formation", { cancelToken: source.token }),
        apiClient.get("/groupes", { cancelToken: source.token }),
        apiClient.get("/jours", { cancelToken: source.token }),
        apiClient.get("/group_jour", { cancelToken: source.token }),
      ]);

      clearTimeout(timeoutId);

      // Validate response structures
      setFormations(
        Array.isArray(formationsResponse.data) ? formationsResponse.data : [],
      );
      setCategories(
        Array.isArray(categoriesResponse.data) ? categoriesResponse.data : [],
      );
      setGroupes(
        Array.isArray(groupesResponse.data) ? groupesResponse.data : [],
      );
      setJours(Array.isArray(joursResponse.data) ? joursResponse.data : []);
      setGroupJour(
        Array.isArray(groupJourResponse.data) ? groupJourResponse.data : [],
      );
    } catch (err) {
      if (axios.isCancel(err)) {
        setError(
          new Error(
            "Délai d'attente dépassé : Veuillez vérifier votre connexion",
          ),
        );
      } else if (err.response) {
        setError(
          new Error(
            `Erreur du serveur : ${err.response.status} - ${
              err.response.data?.message || "Erreur inconnue"
            }`,
          ),
        );
      } else if (err.request) {
        setError(
          new Error(
            "Erreur réseau : Impossible d'atteindre le serveur. Veuillez vérifier votre connexion.",
          ),
        );
      } else {
        setError(new Error(`Erreur : ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    AOS.init();
    fetchData();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  const handleCategoryChange = (categoryName) => {
    setSelectedCategory(categoryName);
  };

  // Fonction pour calculer la capacité totale et l'effectif total pour une formation
  const getFormationCapacityInfo = (formationId) => {
    const formationGroupes = groupes.filter(
      (groupe) => groupe.id_formation === formationId,
    );

    if (formationGroupes.length === 0) {
      return { capaciteTotal: 0, effectifTotal: 0 };
    }

    const capaciteTotal = formationGroupes.reduce(
      (sum, groupe) => sum + groupe.Capacite,
      0,
    );
    const effectifTotal = formationGroupes.reduce(
      (sum, groupe) => sum + groupe.Effectif_Actuel,
      0,
    );

    return { capaciteTotal, effectifTotal };
  };

  const filteredFormations =
    selectedCategory === "all"
      ? formations
      : formations.filter(
          (formation) => formation.id_cat_for === selectedCategory,
        );

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des formations...</p>
        </div>
      </div>
    );
  }

  // Show full page error
  if (error && formations.length === 0 && categories.length === 0) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement</h4>
                <p className="mb-3">{error.message}</p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i>
                      Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <main className="main-area fix">
        {/* Show warning if there was an error but we have some data */}
        {error && (
          <div className="container mt-4">
            <div className="alert alert-warning" role="alert">
              <div className="d-flex justify-content-between align-items-center">
                <small>
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  Données partielles: {error.message}
                </small>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>
                      Chargement...
                    </>
                  ) : (
                    "Actualiser"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        <section
          className="banner-area banner-bg tg-motion-effects"
          data-background="assets/img/banner/banner_bg.png"
        >
          <div className="container">
            <div className="row justify-content-between align-items-start">
              <div className="col-xl-5 col-lg-6">
                <div className="banner__content">
                  <h3
                    className="title tg-svg"
                    data-aos="fade-right"
                    data-aos-delay="400"
                  >
                    Apprendre{" "}
                    <span className="position-relative">
                      <span
                        className="svg-icon"
                        id="banner-svg"
                        data-svg-icon="assets/img/objects/title_shape.svg"
                      ></span>
                      <svg
                        x="0px"
                        y="0px"
                        preserveAspectRatio="none"
                        viewBox="0 0 209 59"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.74438 7.70565C69.7006 -1.18799 136.097 -2.38304 203.934 4.1205C207.178 4.48495 209.422 7.14626 208.933 10.0534C206.793 23.6481 205.415 36.5704 204.801 48.8204C204.756 51.3291 202.246 53.5582 199.213 53.7955C136.093 59.7623 74.1922 60.5985 13.5091 56.3043C10.5653 56.0924 7.84371 53.7277 7.42158 51.0325C5.20725 38.2627 2.76333 25.6511 0.0898448 13.1978C-0.465589 10.5873 1.61173 8.1379 4.73327 7.70565"
                          fill="currentcolor"
                        />
                      </svg>
                      Evoluer
                    </span>
                    <br />
                    <b>Transmettre</b>
                  </h3>
                  <p data-aos="fade-right" data-aos-delay="600">
                    Parce que chaque parcours est différent nous vous guidons
                    dans le vôtre avec passion et engagement.
                  </p>
                  <div
                    className="banner__btn-wrap aos-init aos-animate"
                    data-aos="fade-right"
                    data-aos-delay="800"
                  >
                    <Link to="/courses" className="btn arrow-btn">
                      Tout les cours{" "}
                      <img
                        src="assets/img/icons/right_arrow.svg"
                        alt="img"
                        className="injectable"
                      />{" "}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="banner__images">
                  <img
                    src="assets/img/banner/banner_img.png"
                    alt="img"
                    className="main-img"
                  />
                  <div
                    className="shape big-shape"
                    data-aos="fade-up-right"
                    data-aos-delay="600"
                  >
                    <img
                      src="assets/img/banner/banner_shape01.png"
                      alt="shape"
                      className="tg-motion-effects1"
                    />
                  </div>
                  <img
                    src="assets/img/banner/bg_dots.svg"
                    alt="shape"
                    className="shape bg-dots rotateme"
                  />
                  <img
                    src="assets/img/banner/banner_shape02.png"
                    alt="shape"
                    className="shape small-shape tg-motion-effects3"
                  />
                </div>
              </div>
            </div>
          </div>
          <img
            src="assets/img/banner/banner_shape01.svg"
            alt="shape"
            className="line-shape"
            data-aos="fade-right"
            data-aos-delay="1600"
          />
        </section>
        {/* Loading state for categories section during retry */}
        {loading && retryCount > 0 && (
          <section className="categories-area section-py-120">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-12 text-center">
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <span className="ms-3">Mise à jour des catégories...</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        {!loading && (
          <section className="categories-area section-py-120">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-xl-5 col-lg-7">
                  <div className="section__title text-center mb-40">
                    <h2 className="title">Catégorie de cours</h2>
                    <p className="desc">
                      Découvrez nos différentes formations adaptées à vos
                      besoins
                    </p>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div className="categories__wrap">
                    <Swiper
                      modules={[Navigation]}
                      spaceBetween={20}
                      slidesPerView={5}
                      navigation={{
                        prevEl: ".categories-button-prev",
                        nextEl: ".categories-button-next",
                      }}
                      breakpoints={{
                        0: { slidesPerView: 2 },
                        768: { slidesPerView: 3 },
                        992: { slidesPerView: 5 },
                      }}
                    >
                      {Array.isArray(categories) &&
                        categories.map((category) => (
                          <SwiperSlide key={category.id}>
                            <div className="categories__item">
                              <div className="icon">
                                <img
                                  src={
                                    category.image_cat_formation
                                      ? `${process.env.REACT_APP_API_URL}/uploads/${category.image_cat_formation}`
                                      : "assets/img/icons/features_icon02.svg"
                                  }
                                  alt={category.nom_cat_formation}
                                  className="img-thumbnail rounded-circle"
                                />
                              </div>
                              <span className="name">
                                {category.nom_cat_formation}
                              </span>
                            </div>
                          </SwiperSlide>
                        ))}
                    </Swiper>
                    <div className="categories__nav">
                      <button className="categories-button-prev">
                        <svg
                          width="16"
                          height="14"
                          viewBox="0 0 16 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15 7L1 7M1 7L7 1M1 7L7 13"
                            stroke="#161439"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                      <button className="categories-button-next">
                        <svg
                          width="16"
                          height="14"
                          viewBox="0 0 16 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 7L15 7M15 7L9 1M15 7L9 13"
                            stroke="#161439"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="about-area tg-motion-effects section-py-120">
          <div className="container">
            <div className="row align-items-center justify-content-center">
              <div className="col-lg-6 col-md-9">
                <div className="about__images">
                  <img
                    src="assets/img/others/about_img.png"
                    alt="img"
                    className="main-img"
                  />
                </div>
              </div>
              <div className="col-lg-6">
                <div className="about__content">
                  <div className="section__title">
                    <span className="sub-title">En savoir plus sur nous</span>
                    <h2 className="title">
                      Former la prochaine génération de{" "}
                      <span className="position-relative">
                        <svg
                          x="0px"
                          y="0px"
                          preserveAspectRatio="none"
                          viewBox="0 0 209 59"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M4.74438 7.70565C69.7006 -1.18799 136.097 -2.38304 203.934 4.1205C207.178 4.48495 209.422 7.14626 208.933 10.0534C206.793 23.6481 205.415 36.5704 204.801 48.8204C204.756 51.3291 202.246 53.5582 199.213 53.7955C136.093 59.7623 74.1922 60.5985 13.5091 56.3043C10.5653 56.0924 7.84371 53.7277 7.42158 51.0325C5.20725 38.2627 2.76333 25.6511 0.0898448 13.1978C-0.465589 10.5873 1.61173 8.1379 4.73327 7.70565"
                            fill="currentcolor"
                          />
                        </svg>
                        professionnels
                      </span>{" "}
                      de la technologie
                    </h2>
                  </div>
                  <p className="desc">
                    Chez DVELGE, nous façonnons l'avenir en équipant les
                    individus des compétences essentielles pour le paysage
                    numérique d'aujourd'hui. Nos programmes de formation sur
                    mesure allient flexibilité, expérience pratique et mentorat
                    d'experts pour garantir le succès de chaque participant.
                  </p>
                  <ul className="about__info-list list-wrap">
                    <li className="about__info-list-item">
                      <i className="flaticon-angle-right"></i>
                      <p className="content">Certificats reconnus</p>
                    </li>
                    <li className="about__info-list-item">
                      <i className="flaticon-angle-right"></i>
                      <p className="content">Aide à la recherche d'emploi</p>
                    </li>
                    <li className="about__info-list-item">
                      <i className="flaticon-angle-right"></i>
                      <p className="content">Formateurs hautement qualifiés</p>
                    </li>
                    <li className="about__info-list-item">
                      <i className="flaticon-angle-right"></i>
                      <p className="content">Horaires flexibles et adaptés </p>
                    </li>
                    <li className="about__info-list-item">
                      <i className="flaticon-angle-right"></i>
                      <p className="content">Apprentissage pratique </p>
                    </li>
                  </ul>
                  <div className="tg-button-wrap">
                    <Link to="/courses" className="btn arrow-btn">
                      Tout les cours{" "}
                      <img
                        src="assets/img/icons/right_arrow.svg"
                        alt="img"
                        className="injectable"
                      />{" "}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Loading state for courses section during retry */}
        {loading && retryCount > 0 && (
          <section className="courses-area section-pt-120 section-pb-90">
            <div className="container">
              <div className="row justify-content-center">
                <div className="col-12 text-center">
                  <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    <span className="ms-3">Mise à jour des formations...</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        {!loading && (
          <section
            className="courses-area section-pt-120 section-pb-90"
            data-background="assets/img/bg/courses_bg.jpg"
          >
            <div className="container">
              <div className="section__title-wrap">
                <div className="row justify-content-center">
                  <div className="col-lg-6">
                    <div className="section__title text-center mb-40">
                      <h2 className="title">Découvrez nos Meilleurs Cours</h2>
                      <p className="desc">
                        Trouvez la formation qui vous convient
                      </p>
                    </div>
                    <div className="courses__nav">
                      <ul
                        className="nav nav-tabs"
                        id="courseTab"
                        role="tablist"
                      >
                        <li className="nav-item" role="presentation">
                          <button
                            className={`nav-link ${
                              selectedCategory === "all" ? "active" : ""
                            }`}
                            onClick={() => handleCategoryChange("all")}
                          >
                            Tout les cours
                          </button>
                        </li>
                        {Array.isArray(categories) &&
                          categories.map((category) => (
                            <li
                              className="nav-item"
                              role="presentation"
                              key={category.id_cat_for}
                            >
                              <button
                                className={`nav-link ${
                                  selectedCategory === category.id_cat_for
                                    ? "active"
                                    : ""
                                }`}
                                onClick={() =>
                                  handleCategoryChange(category.id_cat_for)
                                }
                              >
                                {category.nom_cat_formation}
                              </button>
                            </li>
                          ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              <div className="tab-content" id="courseTabContent">
                <div className="tab-pane fade show active" role="tabpanel">
                  <div className="swiper courses-swiper-active">
                    <Swiper
                      spaceBetween={30}
                      slidesPerView={4}
                      autoplay={{
                        delay: 2500,
                        disableOnInteraction: false,
                      }}
                      breakpoints={{
                        0: { slidesPerView: 1 },
                        768: { slidesPerView: 2 },
                        992: { slidesPerView: 3 },
                        1200: { slidesPerView: 4 },
                      }}
                    >
                      {filteredFormations.length > 0 ? (
                        filteredFormations.map((formation) => {
                          const { capaciteTotal, effectifTotal } =
                            getFormationCapacityInfo(formation.id_formation);
                          const percentageFilled =
                            capaciteTotal > 0
                              ? (effectifTotal / capaciteTotal) * 100
                              : 0;

                          return (
                            <SwiperSlide key={formation.id_formation}>
                              <div className="courses__item shine__animate-item">
                                <div className="courses__item-thumb">
                                  <Link
                                    to={`/course-details/${formation.id_formation}`}
                                    className="shine__animate-link"
                                  >
                                    <img
                                      src={
                                        formation.image_formation
                                          ? `${process.env.REACT_APP_API_URL}/uploads/${formation.image_formation}`
                                          : "assets/img/courses/course_thumb01.jpg"
                                      }
                                      alt={formation.nom_formation}
                                    />
                                  </Link>
                                </div>
                                <div className="courses__item-content">
                                  <ul className="courses__item-meta list-wrap">
                                    <li className="courses__item-tag">
                                      <Link to="#">
                                        {formation.mode_livraison}
                                      </Link>
                                    </li>
                                    <li className="avg-rating">
                                      <i className="fas fa-clock"></i>{" "}
                                      {formation.duree_formation}
                                    </li>
                                  </ul>
                                  <h5 className="title">
                                    <Link
                                      to={`/courseDetails/${formation.id_formation}`}
                                    >
                                      {formation.nom_formation}
                                    </Link>
                                  </h5>
                                  <p className="author">
                                    {truncateDescription(
                                      formation.description_formation,
                                      100,
                                    )}
                                  </p>
                                  <div className="courses__item-bottom d-flex justify-content-between align-items-center mt-3">
                                    <Link
                                      to={`/courseDetails/${formation.id_formation}`}
                                      className="btn btn-primary btn-sm d-flex align-items-center"
                                    >
                                      <span className="me-2">Détail</span>
                                      <i className="fas fa-arrow-right"></i>
                                    </Link>
                                    <div className="price text-secondary fw-bold text-nowrap ms-1">
                                      {formation.prix_formation}{" "}
                                      <i className=""> GNF</i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </SwiperSlide>
                          );
                        })
                      ) : (
                        <p>Aucune formation disponible pour cette catégorie.</p>
                      )}
                    </Swiper>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="instructor__area-two">
          <div className="container">
            <div className="instructor__item-wrap-two">
              <div className="row">
                <div className="col-xl-6">
                  <div className="instructor__item-two tg-svg">
                    <div className="instructor__thumb-two">
                      <img
                        src="assets/img/instructor/instructor_two01.png"
                        alt="img"
                      />
                      <div className="shape-one">
                        <img
                          src="assets/img/instructor/instructor_shape01.svg"
                          alt="img"
                          className="injectable"
                        />
                      </div>
                      <div className="shape-two">
                        <span
                          className="svg-icon"
                          id="instructor-svg"
                          data-svg-icon="assets/img/instructor/instructor_shape02.svg"
                        ></span>
                      </div>
                    </div>
                    <div className="instructor__content-two">
                      <h3 className="title">
                        <Link to="contact.html">Devenez Instructeur </Link>
                      </h3>
                      <p>
                        Vous avez envie de partager votre savoir, d'accompagner
                        des talents en devenir et de contribuer à la croissance
                        d'une entreprise dynamique?
                      </p>
                      <div className="tg-button-wrap">
                        <Link
                          to="https://www.dvelgegroup.com/carrieres"
                          className="btn arrow-btn"
                        >
                          Postulez maintenant{" "}
                          <img
                            src="assets/img/icons/right_arrow.svg"
                            alt="img"
                            className="injectable"
                          />{" "}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-6">
                  <div className="instructor__item-two tg-svg">
                    <div className="instructor__thumb-two">
                      <img
                        src="assets/img/instructor/instructor_two02.png"
                        alt="img"
                      />
                      <div className="shape-one">
                        <img
                          src="assets/img/instructor/instructor_shape01.svg"
                          alt="img"
                          className="injectable"
                        />
                      </div>
                      <div className="shape-two">
                        <span
                          className="svg-icon"
                          id="instructor-svg-two"
                          data-svg-icon="assets/img/instructor/instructor_shape02.svg"
                        ></span>
                      </div>
                    </div>
                    <div className="instructor__content-two">
                      <h3 className="title">
                        <Link to="contact.html">Devenir Etudiant </Link>
                      </h3>
                      <p>
                        DVELGE ouvre ses portes aux étudiants passionnés qui
                        souhaitent apprendre, grandir et se préparer aux défis
                        du monde professionnel
                      </p>
                      <div className="tg-button-wrap">
                        <Link to="/courses" className="btn arrow-btn">
                          Postulez maintenant{" "}
                          <img
                            src="assets/img/icons/right_arrow.svg"
                            alt="img"
                            className="injectable"
                          />{" "}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="faq__area">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="faq__img-wrap tg-svg">
                  <div className="faq__img">
                    <img src="assets/img/others/faq_img.png" alt="img" />
                    <div className="shape-one">
                      <img
                        src="assets/img/others/faq_shape01.svg"
                        className="injectable tg-motion-effects4"
                        alt="img"
                      />
                    </div>
                    <div className="shape-two">
                      <span
                        className="svg-icon"
                        id="faq-svg"
                        data-svg-icon="assets/img/others/faq_shape02.svg"
                      ></span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="faq__content">
                  <div className="section__title pb-10">
                    <h2 className="title">
                      {" "}
                      Commencez votre <br /> formation dès aujourd'hui{" "}
                    </h2>
                  </div>
                  <p>
                    Découvrez nos programmes, posez vos questions, et progressez
                    à votre rythme avec Dvelge Formation.
                  </p>
                  <div className="faq__wrap">
                    <div className="accordion" id="accordionExample">
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseOne"
                            aria-expanded="true"
                            aria-controls="collapseOne"
                          >
                            À qui s'adresse cette formation ?
                          </button>
                        </h2>
                        <div
                          id="collapseOne"
                          className="accordion-collapse collapse show"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Nous proposons une gamme de formations adaptées
                              aux différents besoins d'apprentissage. Que vous
                              soyez un individu souhaitant perfectionner vos
                              compétences, une entreprise cherchant à former ses
                              employés ou une personne ayant des besoins
                              spécifiques, nous avons le programme qu'il vous
                              faut.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseTwo"
                            aria-expanded="false"
                            aria-controls="collapseTwo"
                          >
                            Proposez-vous des formations personnalisées ?
                          </button>
                        </h2>
                        <div
                          id="collapseTwo"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Oui. Des formations flexibles adaptées aux besoins
                              spécifiques d'apprentissage. Que ce soit pour des
                              individus ou des équipes, cette option garantit un
                              développement ciblé des compétences à un rythme
                              adapté.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseThree"
                            aria-expanded="false"
                            aria-controls="collapseThree"
                          >
                            Quel est le format de la formation ?
                          </button>
                        </h2>
                        <div
                          id="collapseThree"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Nous proposons des formations en ligne, en
                              présentiel ou en format hybride. Vous pouvez
                              choisir ce qui vous convient le mieux.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseFour"
                            aria-expanded="false"
                            aria-controls="collapseFour"
                          >
                            Combien de temps dure la formation ?
                          </button>
                        </h2>
                        <div
                          id="collapseFour"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>La durée dépend de la formation choisie.</p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseFive"
                            aria-expanded="false"
                            aria-controls="collapseFive"
                          >
                            Quand commencent les prochaines sessions ?
                          </button>
                        </h2>
                        <div
                          id="collapseFive"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Les prochaines dates sont affichées sur chaque
                              fiche formation. Vous pouvez également nous
                              contacter pour organiser une session privée ou en
                              entreprise.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseSix"
                            aria-expanded="false"
                            aria-controls="collapseSix"
                          >
                            Vais-je recevoir une certification ?
                          </button>
                        </h2>
                        <div
                          id="collapseSix"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Oui, à la fin de chaque formation, une
                              certification vous est remise.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseSeven"
                            aria-expanded="false"
                            aria-controls="collapseSeven"
                          >
                            Combien coûte la formation ?
                          </button>
                        </h2>
                        <div
                          id="collapseSeven"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Les tarifs varient selon le format et la durée.
                              Vous pouvez consulter les prix directement sur la
                              page de la formation ou demander un devis
                              personnalisé.
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <button
                            className="accordion-button collapsed"
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target="#collapseEight"
                            aria-expanded="false"
                            aria-controls="collapseEight"
                          >
                            Accompagnement après formation ?
                          </button>
                        </h2>
                        <div
                          id="collapseEight"
                          className="accordion-collapse collapse"
                          data-bs-parent="#accordionExample"
                        >
                          <div className="accordion-body">
                            <p>
                              Oui, nous proposons un suivi post-formation avec
                              un accès à une communauté, un support par e-mail,
                              et dans certains cas, des sessions de coaching
                              personnalisées.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* Nouveau: Ajouter la section des certificats */}
        <CertificatesDisplay />{" "}
        {/* Passez un userId en prop si vous voulez filtrer pour un utilisateur spécifique */}
        {/* Autres sections */}
      </main>
    </>
  );
};

export default Home;
